import React from "react";

export default function PresentationInkLayer({ active, color = "#DA291C", eraser = false }) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);

  React.useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    const frame = canvas?.parentElement;
    if (!canvas || !frame) return undefined;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const oldWidth = Number.parseFloat(canvas.style.width) || 0;
      const oldHeight = Number.parseFloat(canvas.style.height) || 0;
      const previous = canvas.width && canvas.height ? canvas.toDataURL() : null;
      const width = Math.max(frame.scrollWidth, frame.clientWidth, window.innerWidth);
      const height = Math.max(frame.scrollHeight, frame.clientHeight, window.innerHeight);

      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.lineCap = "round";
      context.lineJoin = "round";
      context.lineWidth = 4;
      context.strokeStyle = color;
      context.globalCompositeOperation = eraser ? "destination-out" : "source-over";

      if (previous && oldWidth > 0 && oldHeight > 0) {
        const image = new Image();
        image.onload = () => context.drawImage(image, 0, 0, oldWidth, oldHeight, 0, 0, width, height);
        image.src = previous;
      }
    };

    resize();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    observer?.observe(frame);
    window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [active]);

  const point = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const startDrawing = (event) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    drawingRef.current = true;
    canvas.setPointerCapture?.(event.pointerId);
    const { x, y } = point(event);
    context.beginPath();
    context.moveTo(x, y);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = eraser ? 18 : 4;
    context.strokeStyle = color;
    context.globalCompositeOperation = eraser ? "destination-out" : "source-over";
  };

  const draw = (event) => {
    if (!drawingRef.current) return;
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    const { x, y } = point(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = (event) => {
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  if (!active) return null;
  return (
    <canvas
      ref={canvasRef}
      className="report-presentation-ink-layer"
      aria-label="Presentation drawing layer"
      onPointerDown={startDrawing}
      onPointerMove={draw}
      onPointerUp={stopDrawing}
      onPointerCancel={stopDrawing}
    />
  );
}
